import Dexie from 'dexie';
import { getDb } from './index';
import { getCurrentTenantId } from './tenant';
import type {
  Client,
  Commande,
  CommandePizza,
  Order,
  OrderStatus,
  Pizza,
  StatutCommande,
} from './schema';
import { MAX_DERNIERES_COMMANDES } from './schema';
import { STORAGE_KEY_SEED_VERSION_PREFIX } from './constants';
import { peutTransiterVers } from '../lib/statuts';
import { composeNotes, parseNotes } from '../lib/notes-convention';
import type { CatalogueEntry } from '../config/catalogue.pizznco';

// Wrappers transactionnels des opérations multi-tables critiques (B-001 §2 IN).
// Liste figée : createOrder, updateOrder, refaireLast3, endService — minimum 4
// du body §2. Toute extension future passe par un nouveau wrapper ici.

export interface CreateOrderInput {
  customerName: string;
  items: Array<{ productId: number; qty: number }>;
}

export async function createOrder(
  input: CreateOrderInput,
  tenantId: string = getCurrentTenantId(),
): Promise<number> {
  const db = getDb();
  return db.transaction('rw', db.orders, db.orderItems, async () => {
    const now = Date.now();
    const orderId = await db.orders.add({
      customerName: input.customerName,
      status: 'pending',
      createdAt: now,
      tenantId,
    });
    for (const it of input.items) {
      await db.orderItems.add({
        orderId,
        productId: it.productId,
        qty: it.qty,
        createdAt: now,
        tenantId,
      });
    }
    return orderId;
  });
}

export interface UpdateOrderInput {
  orderId: number;
  patch?: Partial<Pick<Order, 'customerName' | 'status'>>;
  newItems?: Array<{ productId: number; qty: number }>;
  removeItemIds?: number[];
}

export async function updateOrder(
  input: UpdateOrderInput,
  tenantId: string = getCurrentTenantId(),
): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.orders, db.orderItems, async () => {
    const order = await db.orders.get(input.orderId);
    if (!order || order.tenantId !== tenantId) {
      throw new Error(`Order ${input.orderId} not found in scope tenantId=${tenantId}`);
    }
    if (input.patch) {
      await db.orders.update(input.orderId, input.patch);
    }
    if (input.removeItemIds && input.removeItemIds.length > 0) {
      for (const itemId of input.removeItemIds) {
        const it = await db.orderItems.get(itemId);
        if (!it || it.tenantId !== tenantId || it.orderId !== input.orderId) continue;
        await db.orderItems.delete(itemId);
      }
    }
    if (input.newItems && input.newItems.length > 0) {
      const now = Date.now();
      for (const ni of input.newItems) {
        await db.orderItems.add({
          orderId: input.orderId,
          productId: ni.productId,
          qty: ni.qty,
          createdAt: now,
          tenantId,
        });
      }
    }
  });
}

// Reproduit les 3 dernières commandes du tenant courant en tant que nouvelles
// commandes pending. Items copiés à l'identique. Tout dans une seule transaction.
export async function refaireLast3(
  tenantId: string = getCurrentTenantId(),
): Promise<number[]> {
  const db = getDb();
  return db.transaction('rw', db.orders, db.orderItems, async () => {
    const last3 = await db.orders
      .where('[tenantId+createdAt]')
      .between([tenantId, Dexie.minKey], [tenantId, Dexie.maxKey])
      .reverse()
      .limit(3)
      .toArray();

    const newOrderIds: number[] = [];
    const now = Date.now();
    for (const original of last3) {
      const newOrderId = await db.orders.add({
        customerName: original.customerName,
        status: 'pending',
        createdAt: now,
        tenantId,
      });
      const items = await db.orderItems
        .where('[tenantId+orderId]')
        .equals([tenantId, original.id as number])
        .toArray();
      for (const it of items) {
        await db.orderItems.add({
          orderId: newOrderId,
          productId: it.productId,
          qty: it.qty,
          createdAt: now,
          tenantId,
        });
      }
      newOrderIds.push(newOrderId);
    }
    return newOrderIds;
  });
}

// Clôt toutes les commandes ouvertes (pending|inProgress) et marque la session
// active comme terminée. Atomique : si une étape échoue, aucun changement.
export async function endService(
  tenantId: string = getCurrentTenantId(),
): Promise<{ ordersClosed: number; sessionId: number }> {
  const db = getDb();
  return db.transaction('rw', db.orders, db.sessions, async () => {
    const now = Date.now();
    const toClose: Order[] = [];
    for (const status of ['pending', 'inProgress'] as OrderStatus[]) {
      const batch = await db.orders
        .where('[tenantId+status]')
        .equals([tenantId, status])
        .toArray();
      toClose.push(...batch);
    }
    for (const order of toClose) {
      await db.orders.update(order.id as number, { status: 'done', closedAt: now });
    }
    const activeSession = await db.sessions
      .where('tenantId')
      .equals(tenantId)
      .filter((s) => s.endedAt === undefined)
      .first();
    let sessionId: number;
    if (activeSession) {
      await db.sessions.update(activeSession.id as number, { endedAt: now });
      sessionId = activeSession.id as number;
    } else {
      sessionId = await db.sessions.add({ startedAt: now, endedAt: now, tenantId });
    }
    return { ordersClosed: toClose.length, sessionId };
  });
}

// --- T1 sous-ticket 1 — DATA-MODEL T1 wrappers (post-B-001) ------------------
// 4 wrappers ajoutés ici suivent le pattern B-001 ci-dessus :
// - une seule transaction Dexie englobant toutes les écritures atomiques ;
// - scope `tenantId` propagé via `getCurrentTenantId()` (override pour tests) ;
// - aucun champ paiement (frontière anti-caisse brief MVP §1.5).

export interface PizzaInput {
  pizzaId: number;
  qty: number;
  notes_libres?: string;
  prix_unitaire_indicatif: number;
}

export interface CreateCommandeInput {
  telephone: string;
  nom?: string;
  habitude?: string;
  commandes_recurrentes?: string;
  pizzas: PizzaInput[];
  heure_retrait_proposee: number;
  train_id?: number;
}

export interface CreateCommandeResult {
  commandeId: number;
  pizzaIds: number[];
  clientId: number;
  clientReused: boolean;
}

// Crée une Commande complète atomiquement : upsert Client par téléphone (même tenant) +
// Commande + CommandePizza[] + maj LIFO `dernieres_commandes_ids` du Client. Tout dans
// UNE seule transaction Dexie. En cas d'erreur quelconque, rollback complet.
//
// Upsert client (Test #10) : si un Client existe déjà pour `[tenantId, telephone]`, il est
// réutilisé (pas de doublon) ; les champs fournis (nom, habitude, commandes_recurrentes)
// mettent à jour le Client existant — les champs non fournis ne sont jamais écrasés.
export async function createCommande(
  input: CreateCommandeInput,
  tenantId: string = getCurrentTenantId(),
): Promise<CreateCommandeResult> {
  const db = getDb();
  return db.transaction(
    'rw',
    db.clients,
    db.commandes,
    db.commandePizzas,
    async () => {
      const now = Date.now();

      // 1) Upsert Client.
      const existing = await db.clients
        .where('[tenantId+telephone]')
        .equals([tenantId, input.telephone])
        .first();

      let clientId: number;
      let clientReused: boolean;
      let clientNom: string;
      let priorIds: number[];

      if (existing && typeof existing.id === 'number') {
        clientReused = true;
        clientId = existing.id;
        clientNom = input.nom ?? existing.nom;
        priorIds = existing.dernieres_commandes_ids;
        const patch: Partial<Omit<Client, 'tenantId' | 'id'>> = { updatedAt: now };
        if (input.nom !== undefined) patch.nom = input.nom;
        if (input.habitude !== undefined) patch.habitude = input.habitude;
        if (input.commandes_recurrentes !== undefined)
          patch.commandes_recurrentes = input.commandes_recurrentes;
        await db.clients.update(clientId, patch);
      } else {
        clientReused = false;
        clientNom = input.nom ?? '';
        priorIds = [];
        const newClient: Omit<Client, 'id'> = {
          tenantId,
          telephone: input.telephone,
          nom: clientNom,
          dernieres_commandes_ids: [],
          createdAt: now,
          updatedAt: now,
          ...(input.habitude !== undefined ? { habitude: input.habitude } : {}),
          ...(input.commandes_recurrentes !== undefined
            ? { commandes_recurrentes: input.commandes_recurrentes }
            : {}),
        };
        clientId = await db.clients.add(newClient as Client);
      }

      // 2) Création Commande (statut initial 'commandee').
      const newCommande: Omit<Commande, 'id'> = {
        tenantId,
        clientId,
        client_telephone_snapshot: input.telephone,
        client_nom_snapshot: clientNom,
        statut: 'commandee',
        heure_retrait_proposee: input.heure_retrait_proposee,
        createdAt: now,
        updatedAt: now,
        ...(input.train_id !== undefined ? { train_id: input.train_id } : {}),
      };
      const commandeId = await db.commandes.add(newCommande as Commande);

      // 3) Création CommandePizza[] en bulk.
      const pizzaIds: number[] = [];
      for (const p of input.pizzas) {
        const id = await db.commandePizzas.add({
          tenantId,
          commandeId,
          pizzaId: p.pizzaId,
          qty: p.qty,
          prix_unitaire_indicatif: p.prix_unitaire_indicatif,
          createdAt: now,
          ...(p.notes_libres !== undefined ? { notes_libres: p.notes_libres } : {}),
        });
        pizzaIds.push(id);
      }

      // 4) Maj LIFO `dernieres_commandes_ids` du Client (max 3 — Q3 acté Hamid).
      const nextIds = [commandeId, ...priorIds].slice(0, MAX_DERNIERES_COMMANDES);
      await db.clients.update(clientId, { dernieres_commandes_ids: nextIds });

      return { commandeId, pizzaIds, clientId, clientReused };
    },
  );
}

// Met à jour le statut d'une Commande après validation de la transition par la
// matrice data canon W.9 (`src/lib/statuts.ts`). Statuts terminaux verrouillés.
// Erreur claire si transition invalide : caller responsable d'attraper.
export async function updateCommandeStatut(
  commandeId: number,
  nouveauStatut: StatutCommande,
  tenantId: string = getCurrentTenantId(),
): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.commandes, async () => {
    const commande = await db.commandes.get(commandeId);
    if (!commande || commande.tenantId !== tenantId) {
      throw new Error(
        `Commande ${commandeId} not found in scope tenantId=${tenantId}`,
      );
    }
    if (!peutTransiterVers(commande.statut, nouveauStatut)) {
      throw new Error(
        `Transition invalide ${commande.statut} -> ${nouveauStatut}`,
      );
    }
    await db.commandes.update(commandeId, {
      statut: nouveauStatut,
      updatedAt: Date.now(),
    });
  });
}

// Lecture indexée d'un Client par téléphone, scope tenantId. Utilisé en sous-ticket 5
// (CLIENT-MEMOIRE) pour la mémoire client. Index composite `[tenantId+telephone]`
// garantit pas de leak cross-tenant et perf cible < 100 ms sur 1000 clients (Test #9).
export async function findClientByTelephone(
  telephone: string,
  tenantId: string = getCurrentTenantId(),
): Promise<Client | null> {
  const db = getDb();
  const found = await db.clients
    .where('[tenantId+telephone]')
    .equals([tenantId, telephone])
    .first();
  return found ?? null;
}

// Retourne les Commandes en file d'attente (statut ouvert), triées par
// `heure_retrait_proposee` ASC. Filtre les statuts terminaux (`cloturee`, `annulee`)
// + les Commandes des autres tenants.
export async function getCommandesEnFile(
  tenantId: string = getCurrentTenantId(),
): Promise<Commande[]> {
  const db = getDb();
  const all = await db.commandes
    .where('tenantId')
    .equals(tenantId)
    .filter((c) => c.statut !== 'cloturee' && c.statut !== 'annulee')
    .toArray();
  all.sort((a, b) => a.heure_retrait_proposee - b.heure_retrait_proposee);
  return all;
}

// --- SOCLE C — LOT 2 : wrappers additifs (tech-design §3.3/§5) ----------------
// Même pattern maison : une transaction Dexie par écriture atomique, scope tenant
// via getCurrentTenantId() (override tests), aucun champ paiement. Les fonctions
// existantes ci-dessus restent intouchées.

// Statuts « ouverts » (non terminaux) — hors file/four.
const STATUTS_TERMINAUX: readonly StatutCommande[] = ['cloturee', 'annulee'];

function estOuverte(statut: StatutCommande): boolean {
  return !STATUTS_TERMINAUX.includes(statut);
}

// --- seedCatalogue (§3.3) -----------------------------------------------------

function lireMarqueurSeed(key: string): number {
  if (typeof globalThis.localStorage === 'undefined') return 0;
  const raw = globalThis.localStorage.getItem(key);
  if (raw === null) return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : 0;
}

function ecrireMarqueurSeed(key: string, version: number): void {
  if (typeof globalThis.localStorage === 'undefined') return;
  globalThis.localStorage.setItem(key, String(version));
}

export interface SeedCatalogueResult {
  seeded: boolean;
  added: number;
  updated: number;
}

// Seed idempotent, tenant-scopé, NON destructif (jamais delete/clear).
// Marqueur localStorage `tablier:seedVersion:<tenantId>` : si ≥ version → no-op.
// Sinon lookup tenant-scopé par `nom` : existe → update ; absent → add.
//
// ⚠️ VIGILANCE ① : le marqueur de version est écrit UNIQUEMENT APRÈS la résolution
// complète de la transaction Dexie — jamais dedans. Si la transaction échoue, le
// marqueur reste inchangé et le prochain boot re-tentera le seed.
export async function seedCatalogue(
  catalogue: readonly CatalogueEntry[],
  version: number,
  tenantId: string = getCurrentTenantId(),
): Promise<SeedCatalogueResult> {
  const markerKey = STORAGE_KEY_SEED_VERSION_PREFIX + tenantId;
  if (lireMarqueurSeed(markerKey) >= version) {
    return { seeded: false, added: 0, updated: 0 };
  }

  const db = getDb();
  const result = await db.transaction('rw', db.pizzas, async () => {
    const now = Date.now();
    let added = 0;
    let updated = 0;
    for (const entry of catalogue) {
      // Lookup tenant-scopé par `nom` (pas d'index dédié — INV-A : schéma inchangé).
      const existing = await db.pizzas
        .where('tenantId')
        .equals(tenantId)
        .filter((p) => p.nom === entry.nom)
        .first();
      if (existing && typeof existing.id === 'number') {
        const patch: Partial<Omit<Pizza, 'tenantId' | 'id'>> = {
          prix_indicatif: entry.prix_indicatif,
          actif: entry.actif,
          updatedAt: now,
          ...(entry.description !== undefined
            ? { description: entry.description }
            : {}),
        };
        await db.pizzas.update(existing.id, patch);
        updated++;
      } else {
        const nouvelle: Omit<Pizza, 'id'> = {
          tenantId,
          nom: entry.nom,
          prix_indicatif: entry.prix_indicatif,
          actif: entry.actif,
          createdAt: now,
          updatedAt: now,
          ...(entry.description !== undefined
            ? { description: entry.description }
            : {}),
        };
        await db.pizzas.add(nouvelle as Pizza);
        added++;
      }
    }
    return { added, updated };
  });

  // APRÈS le commit uniquement (vigilance ①).
  ecrireMarqueurSeed(markerKey, version);
  return { seeded: true, ...result };
}

// --- createCommandeSansClient (§5.1) -----------------------------------------

export interface CreateCommandeSansClientInput {
  pizzas: PizzaInput[];
  heure_retrait_proposee: number;
  train_id?: number;
}

// Commande « walk-in » anonyme : UNE transaction (commandes + commandePizzas).
// `db.clients` n'est NI lu NI écrit ; LIFO `dernieres_commandes_ids` jamais touché.
// `clientId` et snapshots ABSENTS (champs optionnels non posés). Statut 'commandee'.
export async function createCommandeSansClient(
  input: CreateCommandeSansClientInput,
  tenantId: string = getCurrentTenantId(),
): Promise<{ commandeId: number; pizzaIds: number[] }> {
  const db = getDb();
  return db.transaction('rw', db.commandes, db.commandePizzas, async () => {
    const now = Date.now();
    const nouvelleCommande: Omit<Commande, 'id'> = {
      tenantId,
      statut: 'commandee',
      heure_retrait_proposee: input.heure_retrait_proposee,
      createdAt: now,
      updatedAt: now,
      ...(input.train_id !== undefined ? { train_id: input.train_id } : {}),
    };
    const commandeId = await db.commandes.add(nouvelleCommande as Commande);

    const pizzaIds: number[] = [];
    for (const p of input.pizzas) {
      const id = await db.commandePizzas.add({
        tenantId,
        commandeId,
        pizzaId: p.pizzaId,
        qty: p.qty,
        prix_unitaire_indicatif: p.prix_unitaire_indicatif,
        createdAt: now,
        ...(p.notes_libres !== undefined ? { notes_libres: p.notes_libres } : {}),
      });
      pizzaIds.push(id);
    }
    return { commandeId, pizzaIds };
  });
}

// --- refairePizza (§5.2) ------------------------------------------------------

// Candidates REFAIRE : CommandePizza des commandes ∈ {en_finalisation, prete,
// cloturee}, triées commande.updatedAt DESC puis commandePizza.id DESC, 3 premières
// (la 1ère = défaut UI). Lecture pure tenant-scopée.
export async function getRefaireCandidates(
  tenantId: string = getCurrentTenantId(),
): Promise<CommandePizza[]> {
  const db = getDb();
  const eligibles: readonly StatutCommande[] = [
    'en_finalisation',
    'prete',
    'cloturee',
  ];
  const commandes = await db.commandes
    .where('tenantId')
    .equals(tenantId)
    .filter((c) => eligibles.includes(c.statut))
    .toArray();
  const updatedAtParId = new Map<number, number>();
  for (const c of commandes) {
    if (typeof c.id === 'number') updatedAtParId.set(c.id, c.updatedAt);
  }
  const lignes = (
    await db.commandePizzas.where('tenantId').equals(tenantId).toArray()
  ).filter((p) => updatedAtParId.has(p.commandeId));
  lignes.sort((a, b) => {
    const ua = updatedAtParId.get(a.commandeId) ?? 0;
    const ub = updatedAtParId.get(b.commandeId) ?? 0;
    if (ub !== ua) return ub - ua; // commande.updatedAt DESC
    return (b.id as number) - (a.id as number); // commandePizza.id DESC
  });
  return lignes.slice(0, 3);
}

// Refait UNE pizza (qty=1) dans une NOUVELLE Commande, statut 'priorisee',
// positionnée strictement première. Copiés : pizzaId, prix, texte libre + mod D6,
// clientId + snapshots de l'origine. Imposés : ids/createdAt/updatedAt neufs,
// flag ['refaite'] SUR LA LIGNE créée, train_id NON copié, LIFO client intact.
//
// ⚠️ VIGILANCE ② : file ouverte vide → heure_retrait_proposee = now − 60 000 ;
// sinon min(now, min heures ouvertes) − 60 000.
export async function refairePizza(
  commandePizzaId: number,
  tenantId: string = getCurrentTenantId(),
): Promise<{ commandeId: number; commandePizzaId: number }> {
  const db = getDb();
  return db.transaction('rw', db.commandes, db.commandePizzas, async () => {
    const source = await db.commandePizzas.get(commandePizzaId);
    if (!source || source.tenantId !== tenantId) {
      throw new Error(
        `CommandePizza ${commandePizzaId} not found in scope tenantId=${tenantId}`,
      );
    }
    const origine = await db.commandes.get(source.commandeId);
    if (!origine || origine.tenantId !== tenantId) {
      throw new Error(
        `Commande ${source.commandeId} not found in scope tenantId=${tenantId}`,
      );
    }
    const now = Date.now();

    // Position strictement première.
    const heuresOuvertes = (
      await db.commandes
        .where('tenantId')
        .equals(tenantId)
        .filter((c) => estOuverte(c.statut))
        .toArray()
    ).map((c) => c.heure_retrait_proposee);
    const heure =
      (heuresOuvertes.length === 0
        ? now // vigilance ② : file ouverte vide
        : Math.min(now, ...heuresOuvertes)) - 60_000;

    // Nouvelle Commande (clients hors transaction → LIFO intact).
    const nouvelleCommande: Omit<Commande, 'id'> = {
      tenantId,
      statut: 'priorisee',
      heure_retrait_proposee: heure,
      createdAt: now,
      updatedAt: now,
      ...(origine.clientId !== undefined ? { clientId: origine.clientId } : {}),
      ...(origine.client_telephone_snapshot !== undefined
        ? { client_telephone_snapshot: origine.client_telephone_snapshot }
        : {}),
      ...(origine.client_nom_snapshot !== undefined
        ? { client_nom_snapshot: origine.client_nom_snapshot }
        : {}),
    };
    const commandeId = await db.commandes.add(nouvelleCommande as Commande);

    // Ligne copiée : texte libre + mod copiés, flag ['refaite'] imposé (D6).
    const parsed = parseNotes(source.notes_libres ?? '');
    const notes_libres = composeNotes(parsed.texte, {
      mod: parsed.mod,
      flag: ['refaite'],
    });
    const nouvellePizzaId = await db.commandePizzas.add({
      tenantId,
      commandeId,
      pizzaId: source.pizzaId,
      qty: 1,
      prix_unitaire_indicatif: source.prix_unitaire_indicatif,
      notes_libres,
      createdAt: now,
    });

    return { commandeId, commandePizzaId: nouvellePizzaId };
  });
}

// --- endServiceT1 (§5.3) ------------------------------------------------------

export interface EndServiceT1Result {
  fenetre: { debut: number; fin: number };
  totaux: {
    commandes: number;
    pizzasVendues: number;
    commandesAnnulees: number;
    pizzasRefaites: number;
  };
  duree: number;
  sessionId: number;
  nettoyees: number;
}

// Bilan de fin de service T1 (une transaction commandes + commandePizzas + sessions).
// 1) Session active (endedAt undefined, startedAt DESC) sinon fenêtre du jour
//    [00:00 locale, now] + Session créée.
// 2) Fenêtre = commandes du tenant, createdAt ∈ [début, now].
// 3) Nettoyage via updateCommandeStatut EXCLUSIVEMENT (sous-transaction Dexie).
// 4) Agrégats post-nettoyage (pizzas vendues = Σ qty lignes des cloturee ;
//    pizzas refaites = lignes dont parseNotes(notes_libres).flag ∋ 'refaite').
// 5) Clôture session.
export async function endServiceT1(
  tenantId: string = getCurrentTenantId(),
): Promise<EndServiceT1Result> {
  const db = getDb();
  return db.transaction(
    'rw',
    db.commandes,
    db.commandePizzas,
    db.sessions,
    async () => {
      const now = Date.now();

      // 1) Résolution session / fenêtre.
      const active = (
        await db.sessions
          .where('tenantId')
          .equals(tenantId)
          .filter((s) => s.endedAt === undefined)
          .toArray()
      ).sort((a, b) => b.startedAt - a.startedAt)[0];

      let debut: number;
      let sessionId: number;
      let sessionAResoudre = false;

      if (active && typeof active.id === 'number') {
        debut = active.startedAt;
        sessionId = active.id;
        sessionAResoudre = true;
      } else {
        // Sans session : fenêtre = [00:00 locale, now].
        const minuit = new Date(now);
        minuit.setHours(0, 0, 0, 0);
        const debutJour = minuit.getTime();
        const duJour = await db.commandes
          .where('tenantId')
          .equals(tenantId)
          .filter((c) => c.createdAt >= debutJour && c.createdAt <= now)
          .toArray();
        const startedAt =
          duJour.length > 0
            ? Math.min(...duJour.map((c) => c.createdAt))
            : now;
        debut = startedAt;
        sessionId = await db.sessions.add({ tenantId, startedAt, endedAt: now });
      }

      // 2) Fenêtre (avant nettoyage).
      const fenetreAvant = await db.commandes
        .where('tenantId')
        .equals(tenantId)
        .filter((c) => c.createdAt >= debut && c.createdAt <= now)
        .toArray();

      // 3) Nettoyage via updateCommandeStatut exclusivement (sous-transaction).
      let nettoyees = 0;
      for (const c of fenetreAvant) {
        const id = c.id as number;
        if (c.statut === 'prete') {
          await updateCommandeStatut(id, 'cloturee', tenantId);
          nettoyees++;
        } else if (c.statut === 'en_finalisation') {
          await updateCommandeStatut(id, 'prete', tenantId);
          await updateCommandeStatut(id, 'cloturee', tenantId);
          nettoyees++;
        } else if (
          c.statut === 'commandee' ||
          c.statut === 'priorisee' ||
          c.statut === 'en_preparation' ||
          c.statut === 'enfournee'
        ) {
          await updateCommandeStatut(id, 'annulee', tenantId);
          nettoyees++;
        }
        // cloturee | annulee : déjà terminal, rien.
      }

      // 4) Agrégats post-nettoyage.
      const fenetreApres = await db.commandes
        .where('tenantId')
        .equals(tenantId)
        .filter((c) => c.createdAt >= debut && c.createdAt <= now)
        .toArray();
      const fenetreIds = new Set(fenetreApres.map((c) => c.id as number));
      const clotureeIds = new Set(
        fenetreApres.filter((c) => c.statut === 'cloturee').map((c) => c.id as number),
      );
      const commandesAnnulees = fenetreApres.filter(
        (c) => c.statut === 'annulee',
      ).length;

      const lignes = (
        await db.commandePizzas.where('tenantId').equals(tenantId).toArray()
      ).filter((p) => fenetreIds.has(p.commandeId));
      const pizzasVendues = lignes
        .filter((p) => clotureeIds.has(p.commandeId))
        .reduce((sum, p) => sum + p.qty, 0);
      const pizzasRefaites = lignes.filter((p) =>
        parseNotes(p.notes_libres ?? '').flag.includes('refaite'),
      ).length;

      // 5) Clôture session (le cas sans session est déjà clos à la création).
      if (sessionAResoudre) {
        await db.sessions.update(sessionId, { endedAt: now });
      }

      return {
        fenetre: { debut, fin: now },
        totaux: {
          commandes: fenetreApres.length,
          pizzasVendues,
          commandesAnnulees,
          pizzasRefaites,
        },
        duree: now - debut,
        sessionId,
        nettoyees,
      };
    },
  );
}

// --- SOCLE C — LOT 3.0b : lecture batch des lignes (INV-C) --------------------

// Retourne les CommandePizza de plusieurs commandes en un batch, scope tenant,
// via l'index composite `[tenantId+commandeId]`. Requis par l'UI T1/T2 pour
// afficher les lignes des commandes en file sans jamais toucher une table brute.
export async function getLignesCommandes(
  commandeIds: number[],
  tenantId: string = getCurrentTenantId(),
): Promise<CommandePizza[]> {
  if (commandeIds.length === 0) return [];
  const db = getDb();
  const cles = [...new Set(commandeIds)].map((id) => [tenantId, id]);
  return db.commandePizzas.where('[tenantId+commandeId]').anyOf(cles).toArray();
}
